/**
 * Take care importing code here! This is injected into every app as an individual script tag.
 */
import { getHostname, getTrellisTestDayId, isTrellisTestDay, isWebdriver, setupLocalStorageForTestDay } from './trellis-test-day';
const QA_APP_REGEXP = /(app|local)(-[a-z]+[0-9])?\.hubspotqa\.com/;
const VALID_THEMES = new Set(['trellis-light', 'canvas-light']);
export const DEFAULT_THEME = 'canvas-light';
export const ENABLED_DEPLOYABLES = new Set([
// Test apps that are deployed to prod
'bend-init-test-app', 'bend-init-test-app-no-quartz',
// playlists-ui is opted-in since it is a worker canary app
'playlists-ui',
// Apps that are enabled for the private beta
'crm-index-ui', 'crm-records-ui', 'crm-page-editor-ui', 'crm-record-properties-ui', 'association-history-ui', 'properties-card-properties-editor-ui', 'DashboardUI', 'reporting-discover-ui', 'reports-list', 'advanced-builder', 'kpis-goals-fe', 'object-builder-ui', 'UserPreferencesUI', 'crm-settings-email', 'calling-settings-ui', 'settings-ui-meetings', 'crm-settings-task-reminders', 'settings-ui-security', 'settings-ui-workflows', 'notifications-ui-preferences', 'multi-account-settings-ui', 'settings-ui-account-defaults', 'settings-ui-branding', 'settings-ui-user-defaults', 'settings-ui-multi-currency', 'settings-ui-data-hosting', 'settings-ui-feature-releases', 'PropertySettingsUI', 'object-library-ui', 'object-type-settings', 'association-settings', 'crm-pipeline-settings', 'crm-record-views-ui', 'crm-preview-settings-ui', 'crm-index-settings-ui', 'settings-ui-products', 'payment-links-settings-ui', 'settings-ui-quotes']);
const COOKIE_THEME_KEY = 'hubspot-theme';
const DATASET_THEME_KEY = 'hubspotTheme';
const LOCAL_STORAGE_THEME_KEY = 'hubspot-theme';

// This is used to override the static-app-name check in local storage for testing purposes.
const LOCAL_STORAGE_BYPASS = 'trellis:bypass-static-app-check';

/**
 * The `server-timing` header is set by the static-app-content-worker and
 * contains the deployable name in the format: `d={deployableName}#{orionDeployId}`.
 *
 * The server-timing header is present in the `navigation` timing entry, so this retrieves it
 *
 * We can probably remove this once Quartz/Silo is everywhere
 */
function getStaticAppName() {
  try {
    if (window.performance && performance.getEntriesByType) {
      const navTiming = performance.getEntriesByType('navigation');
      if (navTiming.length > 0) {
        var _appInfoTiming$descri;
        const serverTiming = navTiming[0].serverTiming;
        const appInfoTiming = serverTiming.find(({
          name
        }) => name === 'd');
        const staticAppName = appInfoTiming === null || appInfoTiming === void 0 || (_appInfoTiming$descri = appInfoTiming.description) === null || _appInfoTiming$descri === void 0 ? void 0 : _appInfoTiming$descri.split('#')[0];
        return staticAppName;
      }
    }
  } catch (e) {
    // do nothing
  }
  return undefined;
}
function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}
export function getThemeSetting() {
  try {
    // get theme setting from hubspot-theme cookie with higher priority than localStorage
    const themeCookie = getCookie(COOKIE_THEME_KEY);
    const localStorageTheme = localStorage.getItem(LOCAL_STORAGE_THEME_KEY);
    const themeSetting = themeCookie || localStorageTheme;
    if (themeSetting && VALID_THEMES.has(themeSetting)) {
      return themeSetting;
    }
  } catch (e) {
    // do nothing
  }
  return null;
}
function isAppTrellisEnabled() {
  const staticApp = getStaticAppName();
  return staticApp && ENABLED_DEPLOYABLES.has(staticApp);
}
export function isHubspotQA() {
  const hostname = getHostname();
  return QA_APP_REGEXP.test(hostname);
}

/**
 * This function is run in the `hubspot-theme` file which is loaded in the <head /> of apps.
 * This is used to synchronize a users theme setting (cookie/local storage) with the data-hubspot-theme
 * `<html />` tag of the app, which is what ThemeProvider uses to derive the theme, and what the CSS
 * uses to select the correct values for the CSS variables. This code runs very early on the page,
 * and is inlined into every app.
 */
export function setupTheme() {
  try {
    const bypassAppNameCheck = localStorage.getItem(LOCAL_STORAGE_BYPASS) === 'true';
    const isQa = isHubspotQA();
    let defaultTheme = DEFAULT_THEME;

    // Trellis Test Days are only applied in QA.
    if (isQa) {
      const now = new Date();
      const dateInput = {
        day: now.getUTCDay(),
        hour: now.getUTCHours(),
        now: now.getTime()
      };

      // We don't want to apply TTD code to ATs or unit tests
      // ATs will have VR snapshots that rely on Canvas Light theme
      // Unit tests might be relying on style-specific behaviour for their selectors.
      const isTestDay = isTrellisTestDay(dateInput) && !isWebdriver();
      if (isTestDay) {
        const testDayId = getTrellisTestDayId(dateInput);
        setupLocalStorageForTestDay(String(testDayId));
        defaultTheme = 'trellis-light';
      }
    }

    // this should only work for a small subset of apps that are opted-in to the private beta.
    const trellisEnabled = isAppTrellisEnabled() || bypassAppNameCheck;
    if (trellisEnabled || isQa) {
      const themeSetting = getThemeSetting() || defaultTheme;
      document.documentElement.dataset[DATASET_THEME_KEY] = themeSetting;
    }
  } catch (_unused) {
    // do nothing
  }
}